import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import newsService from '../../../services/newsServices';
import { Article } from '../../../types/news.types';
import { formatTimeAgo } from '../../../utils/dateFormatter';
import styles from './Unicorn.module.css';

const Unicorn: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchUnicorn = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await newsService.getArticlesByCategory('unicorn', 1, 10);
        console.log('🦄 Unicorn articles:', response.articles);
        setArticles(response.articles);

        setLoading(false);
      } catch (err: any) {
        console.error('❌ Failed to fetch unicorn:', err);
        setError(err.message || 'Failed to load unicorn');
        setLoading(false);
      }
    };

    fetchUnicorn();
  }, []);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section className={styles.unicornSection}>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleWrapper}>
              <h2 className={styles.sectionTitle}>Unicorn Startups</h2>
            </div>
          </div>
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading unicorn startups...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || articles.length === 0) {
    return null;
  }

  return (
    <section className={styles.unicornSection}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleWrapper}>
            <h2 className={styles.sectionTitle}>Unicorn Startups</h2>
          </div>
          <Link to="/category/unicorn" className={styles.viewAllButton}>
            View All
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12h14M12 5l7 7-7 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>

        <div className={styles.scrollWrapper}>
          <button
            className={`${styles.scrollButton} ${styles.scrollButtonLeft}`}
            onClick={scrollLeft}
            aria-label="Scroll left"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div className={styles.cardsContainer} ref={scrollContainerRef}>
            {articles.map((article) => (
              <Link
                key={article.id}
                to={`/news/${article.slug}`}
                className={styles.card}
              >
                <div className={styles.imageWrapper}>
                  <img
                    src={article.featuredImage.url}
                    alt={article.featuredImage.alt || article.headline}
                    loading="lazy"
                  />
                  <span className={styles.categoryBadge}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                        fill="currentColor"
                      />
                    </svg>
                    Unicorn
                  </span>
                </div>

                <div className={styles.cardContent}>
                  <h3 className={styles.cardTitle}>{article.headline}</h3>
                  <div className={styles.cardMeta}>
                    <span className={styles.author}>{article.author?.name || 'Unknown'}</span>
                    <span className={styles.separator}>•</span>
                    <span className={styles.time}>
                      {formatTimeAgo(article.publishedAt || article.createdAt)}
                    </span>
                  </div>
                  <div className={styles.cardStats}>
                    <span className={styles.stat}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                      </svg>
                      {article.views.toLocaleString()}
                    </span>
                    <span className={styles.stat}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                      {article.likes}
                    </span>
                  </div>
                </div>
              </Link>
            ))}

            <div className={styles.viewAllIconWrapper}>
              <Link to="/category/unicorn" className={styles.viewAllIcon}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12h14M12 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
              <span className={styles.viewAllLabel}>View All</span>
            </div>
          </div>

          <button
            className={`${styles.scrollButton} ${styles.scrollButtonRight}`}
            onClick={scrollRight}
            aria-label="Scroll right"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18l6-6-6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Unicorn;